import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { Food, Nutrients, getGrossMismatchExpectedCalories, hasNoNutritionData } from '@cf-shared/data-model/food';
import { isRecipe } from '@cf-shared/data-model/recipe';
import { scaleNutrientsToPer100g } from 'util/nutrient-scaling';

/**
 * What a food card on the Foods page needs to know about a food's nutrition, worked out once
 * when the full food has loaded.
 *
 * Kept out of `FoodsPage` so it can be tested: the page pulls in the whole Angular dependency
 * graph and cannot be loaded under the unit test runner.
 */
export interface FoodCardNutrition {
  /** what 100 g of the food holds; the card scales these to whatever it displays */
  nutrientsPer100g: Nutrients;
  /** #4541: the tooltip for the red "Check Values" badge, or undefined when the food looks fine */
  inconsistentNutrientsInfo: TranslatedString | undefined;
  /** true when the card should carry the red "No Nutrition Data" badge */
  noNutritionData: boolean;
}

/** works out the card's per-100 g values, Check Values tooltip and No Nutrition Data flag for a loaded food */
export function describeFoodCardNutrition(food: Food): FoodCardNutrition {
  // a recipe with no usable total weight has no per-100 g values at all: nothing to divide by.
  // every value is NaN, which the card's formatter prints as "--", and the card says there is
  // no nutrition data. the per-100 g object stays SET on purpose -- see foodNutrientsPer100g.
  if (isRecipeWithoutTotalWeight(food)) {
    return {
      nutrientsPer100g: unknownNutrients(food.nutrients),
      inconsistentNutrientsInfo: undefined,
      noNutritionData: true,
    };
  }
  const nutrientsPer100g = foodNutrientsPer100g(food);
  return {
    nutrientsPer100g,
    inconsistentNutrientsInfo: describeInconsistentNutrients(nutrientsPer100g),
    // from the stored nutrients rather than the per-100 g copy: dividing turns a stored
    // `null` into 0, which would hide a recipe that genuinely records nothing
    noNutritionData: hasNoNutritionData(food.nutrients),
  };
}

/**
 * True for a recipe whose total weight is missing, 0, negative or not a number.
 *
 * `ensureRecipeTotalWeight` recomputes a missing or zero total from the ingredients, but a
 * recipe with no ingredients left (or only zero-weight ones) still comes out at 0. The recipe
 * editor refuses to save a recipe with no ingredients, so this is old or synced data -- but a
 * document can be anything an older app version wrote, and the card must not divide by it.
 */
function isRecipeWithoutTotalWeight(food: Food): boolean {
  if (!isRecipe(food)) {
    return false;
  }
  const totalWeight_g = food.totalWeight_g;
  return !(typeof totalWeight_g === 'number' && isFinite(totalWeight_g) && totalWeight_g > 0);
}

/** the same nutrient keys, every value NaN: "this value cannot be known" */
function unknownNutrients(nutrients: Nutrients | undefined): Nutrients {
  const unknown: Nutrients = {};
  for (const key of Object.keys(nutrients ?? {})) {
    unknown[key] = NaN;
  }
  return unknown;
}

/**
 * The tooltip for the red warning badge on a food whose stated calories cannot match its
 * own macros, or undefined when the food looks fine. #4541.
 *
 * Nutrients must be per 100 g -- the check has an absolute kcal floor in it, so a recipe
 * measured per its total weight would be judged against the wrong scale.
 */
function describeInconsistentNutrients(nutrientsPer100g: Nutrients): TranslatedString | undefined {
  const expectedCalories = getGrossMismatchExpectedCalories(nutrientsPer100g);
  if (expectedCalories === undefined) {
    return undefined;
  }
  return LL.foods.badges.checkValuesInfo({ expectedCalories });
}

/**
 * What 100 grams of this food holds.
 *
 * An ordinary food already stores its nutrients per 100 g, so they are copied as they are.
 * A recipe stores the totals for the whole thing, so they are divided by its total weight.
 * A recipe with no usable total weight never reaches here; `describeFoodCardNutrition` handles
 * it first (deferred finding df-bcdbd28b30be). It must not be handled by leaving the per-100 g
 * values unset: the card then falls back to the numbers the search result header carried,
 * which for a recipe are the totals for the WHOLE thing -- a plausible 2400 kcal beside a
 * weight of 100 g.
 *
 * The result is always a NEW object. The food handed in is the app's single shared copy of
 * that document, so scaling its nutrients in place would hand every other reader of it
 * serving-sized values labelled as per-100 g ones.
 */
function foodNutrientsPer100g(food: Food): Nutrients {
  if (!isRecipe(food)) {
    return { ...food.nutrients };
  }
  return scaleNutrientsToPer100g(food.nutrients, food.totalWeight_g);
}
